import React, { useEffect, useState } from 'react';
import { PlayIcon, VideoIcon, Volume2Icon, MaximizeIcon } from 'lucide-react';
interface VideoReplayProps {
  /** URL de Vimeo, Wistia, YouTube o archivo propio. */
  url?: string;
  thumbnailUrl?: string;
  caption?: string;
  title?: string;
  /** Texto superpuesto sobre la miniatura, editable desde el constructor. */
  overlayTitle?: React.ReactNode;
  className?: string;
  /** Ancho máximo del reproductor. */
  maxWidthClass?: string;
  /**
   * true solo dentro del constructor: muestra el aviso de que falta la URL.
   * En la landing pública nunca aparece ningún mensaje interno.
   */
  editing?: boolean;
  /** Marco configurable desde el editor: esquinas, borde y sombra. */
  frame?: VideoFrame;
}
export interface VideoFrame {
  /** Redondeo en px por dispositivo. 0 deja el vídeo totalmente cuadrado. */
  radius?: number;
  radiusTablet?: number;
  radiusMobile?: number;
  /** Redondeo por esquina: «16 16 0 0». Tiene prioridad sobre `radius`. */
  radiusCustom?: string;
  borderWidth?: number;
  borderColor?: string;
  shadow?: 'ninguna' | 'suave' | 'media' | 'fuerte';
  /** Ancho máximo en px; si falta se usa `maxWidthClass`. */
  maxWidth?: number;
}
const SHADOWS: Record<string, string> = {
  ninguna: 'none',
  suave: '0 18px 60px -24px rgba(15,23,42,0.35)',
  media: '0 26px 80px -28px rgba(15,23,42,0.45)',
  fuerte: '0 34px 110px -30px rgba(15,23,42,0.6)'
};

/** Redondeo aplicable al ancho actual de la ventana, sin duplicar estilos. */
function useFrameRadius(frame?: VideoFrame): string | undefined {
  const [width, setWidth] = useState(() => typeof window === 'undefined' ? 1440 : window.innerWidth);
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  if (!frame) return undefined;
  if (frame.radiusCustom && frame.radiusCustom.trim()) {
    return frame.radiusCustom.trim().split(/\s+/).slice(0, 4).map(v => `${parseInt(v, 10) || 0}px`).join(' ');
  }
  const value = width < 640 ? frame.radiusMobile ?? frame.radius : width < 1024 ? frame.radiusTablet ?? frame.radius : frame.radius;
  return value === undefined ? undefined : `${value}px`;
}
function toEmbedUrl(url: string): string | null {
  const youtube = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([\w-]{6,})/);
  if (youtube) return `https://www.youtube.com/embed/${youtube[1]}?autoplay=1`;
  const vimeo = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeo) return `https://player.vimeo.com/video/${vimeo[1]}?autoplay=1`;
  return null;
}
function isFileUrl(url: string): boolean {
  return /\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url);
}

/**
 * Reproductor de vídeo reutilizable en proporción 16:9, protagonista del hero.
 * Se usa en la landing de AVA y en cualquier landing con bloque de vídeo.
 */
export function VideoReplay({
  url,
  thumbnailUrl,
  caption,
  title = 'Vídeo replay',
  overlayTitle,
  className = '',
  maxWidthClass = 'max-w-5xl',
  editing = false,
  frame
}: VideoReplayProps) {
  const [playing, setPlaying] = useState(false);
  const embedUrl = url ? toEmbedUrl(url) : null;
  const hasSource = Boolean(url);
  const radius = useFrameRadius(frame);
  // Cuando el editor define un marco propio, se sustituyen las clases por
  // estilos en línea; si no, el vídeo mantiene exactamente su aspecto actual.
  const custom = Boolean(frame && (radius !== undefined || frame.borderWidth || frame.shadow || frame.maxWidth));
  const shellStyle: React.CSSProperties | undefined = custom ? {
    borderRadius: radius,
    borderWidth: frame?.borderWidth ? `${frame.borderWidth}px` : undefined,
    borderStyle: frame?.borderWidth ? 'solid' : undefined,
    borderColor: frame?.borderColor || undefined,
    boxShadow: frame?.shadow ? SHADOWS[frame.shadow] : undefined
  } : undefined;
  return <figure className={`w-full ${frame?.maxWidth ? '' : maxWidthClass} mx-auto ${className}`} style={frame?.maxWidth ? {
    maxWidth: `${frame.maxWidth}px`
  } : undefined} data-id="element-714" data-mp="2s9njb4qrnfht">
      <div className={`ava-video-shell relative w-full overflow-hidden bg-navy ${custom ? '' : 'rounded-2xl ring-1 ring-black/5 sm:rounded-3xl'}`} style={shellStyle} data-id="element-715" data-mp="2g7e57m5io5e5">
        <div className="relative aspect-video w-full" data-id="element-716" data-mp="2beb4mu2bnvog">
          {playing && hasSource ? embedUrl ? <iframe src={embedUrl} title={title} className="absolute inset-0 h-full w-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; picture-in-picture" allowFullScreen data-id="element-717" data-mp="26h1aj13zmvua" /> : isFileUrl(url as string) ? <video src={url} controls autoPlay className="absolute inset-0 h-full w-full object-cover" data-id="element-718" data-mp="1xizcmufjwjb8" /> : <iframe src={url} title={title} className="absolute inset-0 h-full w-full" allowFullScreen data-id="element-719" data-mp="1sjmgtk6pc1wi" /> : <button type="button" onClick={() => hasSource && setPlaying(true)} aria-label={hasSource ? 'Reproducir el vídeo' : 'Vídeo pendiente de configurar'} className="group absolute inset-0 h-full w-full focus:outline-none focus-visible:ring-4 focus-visible:ring-primary/40" data-id="element-720" data-mp="1nkjlwskfrc29">
              {thumbnailUrl ? <img src={thumbnailUrl} alt="" decoding="async" fetchPriority="high" className="h-full w-full object-cover" data-id="element-721" data-mp="3qbe4qol0d67e" /> : <span className="flex h-full w-full flex-col items-center justify-center gap-2 bg-surface text-muted-foreground" data-id="element-722" data-mp="3ucpmk7xn4n51">
                  <VideoIcon className="h-8 w-8" aria-hidden="true" data-id="element-723" data-mp="800rxm87au12" />
                  <span className="text-sm" data-id="element-724" data-mp="81bzlx4rolxg">Miniatura pendiente</span>
                </span>}

              <span className="absolute inset-0 bg-gradient-to-t from-black-premium/35 via-transparent to-transparent" data-id="element-725" data-mp="37gz3esb851ge" />

              {/* Texto sobre la miniatura: anclado arriba y con ancho máximo
                  para no solaparse nunca con el botón de reproducción. */}
              {overlayTitle && <span className="absolute inset-x-0 top-3 mx-auto max-w-[88%] whitespace-pre-line px-4 text-center text-[15px] font-bold leading-[1.2] text-white drop-shadow-[0_2px_14px_rgba(0,0,0,0.45)] sm:top-[14%] sm:max-w-none sm:px-6 sm:text-3xl" data-id="element-726" data-mp="3lcayj0taktxu">
                  {overlayTitle}
                </span>}

              {/* Botón de reproducción */}
              <span className="absolute left-1/2 top-1/2 flex h-[60px] w-[60px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-[0_12px_40px_-8px_rgba(0,0,0,0.45)] transition-transform duration-300 ease-out group-hover:scale-105 sm:h-24 sm:w-24" data-id="element-727" data-mp="61prze1rngb8">
                <span className="absolute inset-0 rounded-full ring-8 ring-white/25" data-id="element-728" data-mp="3oe4die84a2w4" />
                <PlayIcon className="ml-1 h-6 w-6 text-primary sm:h-9 sm:w-9" fill="currentColor" aria-hidden="true" data-id="element-729" data-mp="3jgckfgvqkaqc" />
              </span>

              {/* Barra de controles decorativa */}
              <span className="absolute inset-x-0 bottom-0 flex items-center gap-3 px-4 py-3 text-white/90 sm:px-6 sm:py-4" data-id="element-730" data-mp="3tea6bxq4hvfs">
                <PlayIcon className="h-4 w-4 flex-none" fill="currentColor" aria-hidden="true" data-id="element-731" data-mp="35ne92l2we815" />
                <span className="text-[11px] tabular-nums sm:text-xs" data-id="element-732" data-mp="30oa09vjvycbc">0:00 / 4:37</span>
                <span className="h-1 flex-1 overflow-hidden rounded-full bg-white/30" data-id="element-733" data-mp="1g6sjkrqbgplz">
                  <span className="block h-full w-[4%] rounded-full bg-white" data-id="element-734" data-mp="1kxr1fvqydvrc" />
                </span>
                <Volume2Icon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-735" data-mp="169mq5cu8cmil" />
                <MaximizeIcon className="h-4 w-4 flex-none" aria-hidden="true" data-id="element-736" data-mp="1b87fgh7y1ls9" />
              </span>

              {/* Aviso solo visible dentro del constructor. */}
              {!hasSource && editing && <span className="absolute inset-x-4 bottom-12 mx-auto w-fit rounded-full bg-white/90 px-3 py-1 text-center text-xs font-medium text-muted-foreground" data-id="element-737" data-mp="1yvz50mihvvbh">
                  Añade la URL del vídeo desde el editor
                </span>}
            </button>}
        </div>
      </div>

      {caption && <figcaption className="mx-auto mt-8 max-w-2xl text-center text-base text-muted-foreground sm:text-lg" data-id="element-738" data-mp="1u0jofmt3rlbd">
          {caption}
        </figcaption>}
    </figure>;
}